import Spinner from "../../common/components/Spinner";
import StatusFlag from "../models/StatusFlag";
import I18n from "i18n-js";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { formatDateTime } from "../../common/utils/dateUtils";
import React from "react";
import PropTypes from "prop-types";

const triggers = ["focus", "hover", "click"];

TrackdechetsStatusBadgeWithTooltip.propTypes = {
  type: PropTypes.string.isRequired,
  tooltipTitle: PropTypes.string.isRequired,
  badgeLabel: PropTypes.string.isRequired,
  lastUpdatedAt: PropTypes.instanceOf(Date),
  children: PropTypes.element,
};

export function TrackdechetsStatusBadgeWithTooltip({
  type,
  tooltipTitle,
  badgeLabel,
  lastUpdatedAt = null,
  children,
}) {
  return (
    <OverlayTrigger
      trigger={triggers}
      placement="auto"
      delay={{ show: 10, hide: 150 }}
      overlay={
        <Tooltip style={{ position: "fixed" }}>
          {tooltipTitle}
          <br />
          {children}
          {lastUpdatedAt && (
            <small>
              {I18n.t(
                "trackdechets.sync_statuses_table.cell.badges.invalid.lastUpdatedAt",
                {
                  date: formatDateTime(lastUpdatedAt),
                },
              )}{" "}
            </small>
          )}
        </Tooltip>
      }
    >
      <span tabIndex={1} className={`badge bg-${type}`}>
        {badgeLabel}
      </span>
    </OverlayTrigger>
  );
}

CompanySyncStatusCell.propTypes = {
  bitFlags: PropTypes.number,
  loading: PropTypes.bool,
  lastUpdatedAt: PropTypes.instanceOf(Date),
  error: PropTypes.bool,
  refresh: PropTypes.func.isRequired,
};

export function CompanySyncStatusCell({
  bitFlags = null,
  lastUpdatedAt = null,
  error = false,
  loading = false,
  refresh,
}) {
  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <TrackdechetsStatusBadgeWithTooltip
        type="danger"
        tooltipTitle={I18n.t(
          "trackdechets.sync_statuses_table.cell.badges.error.title",
        )}
        badgeLabel={I18n.t(
          "trackdechets.sync_statuses_table.cell.badges.error.value",
        )}
        lastUpdatedAt={lastUpdatedAt}
      />
    );
  }

  const flagBag = new StatusFlag(bitFlags || StatusFlag.NONE);

  let badge;

  switch (true) {
    case [null, undefined].includes(bitFlags):
      badge = (
        <TrackdechetsStatusBadgeWithTooltip
          type="dark"
          tooltipTitle={I18n.t(
            "trackdechets.sync_statuses_table.cell.badges.unknown.title",
          )}
          badgeLabel={I18n.t(
            "trackdechets.sync_statuses_table.cell.badges.unknown.value",
          )}
          lastUpdatedAt={lastUpdatedAt}
        />
      );
      break;

    case bitFlags === StatusFlag.NONE:
      badge = (
        <TrackdechetsStatusBadgeWithTooltip
          type="success"
          tooltipTitle={I18n.t(
            "trackdechets.sync_statuses_table.cell.badges.valid.title",
          )}
          badgeLabel={I18n.t(
            "trackdechets.sync_statuses_table.cell.badges.valid.value",
          )}
          lastUpdatedAt={lastUpdatedAt}
        />
      );
      break;

    case flagBag.hasFlag(StatusFlag.FORBIDDEN):
      badge = (
        <TrackdechetsStatusBadgeWithTooltip
          type="danger"
          tooltipTitle={I18n.t(
            new StatusFlag(StatusFlag.FORBIDDEN).getReadable(),
          )}
          badgeLabel={I18n.t(
            "trackdechets.sync_statuses_table.cell.badges.forbidden",
          )}
          lastUpdatedAt={lastUpdatedAt}
        />
      );
      break;

    case flagBag.hasFlag(StatusFlag.SIRET_NOT_FOUND):
      badge = (
        <TrackdechetsStatusBadgeWithTooltip
          type="danger"
          tooltipTitle={I18n.t(
            new StatusFlag(StatusFlag.SIRET_NOT_FOUND).getReadable(),
          )}
          badgeLabel={I18n.t(
            "trackdechets.sync_statuses_table.cell.badges.siret_not_found",
          )}
          lastUpdatedAt={lastUpdatedAt}
        />
      );
      break;

    case flagBag.hasFlag(StatusFlag.NOT_REGISTERED):
      badge = (
        <TrackdechetsStatusBadgeWithTooltip
          type="danger"
          tooltipTitle={I18n.t(
            new StatusFlag(StatusFlag.NOT_REGISTERED).getReadable(),
          )}
          badgeLabel={I18n.t(
            "trackdechets.sync_statuses_table.cell.badges.not_registered",
          )}
          lastUpdatedAt={lastUpdatedAt}
        />
      );
      break;

    default:
      badge = (
        <OverlayTrigger
          trigger={triggers}
          placement="auto"
          delay={{ show: 10, hide: 150 }}
          overlay={
            <Popover style={{ position: "fixed" }}>
              <Popover.Header as="h5">
                {I18n.t(
                  "trackdechets.sync_statuses_table.cell.badges.invalid.title",
                )}
              </Popover.Header>
              <Popover.Body>
                {I18n.t(
                  "trackdechets.sync_statuses_table.cell.badges.invalid.content",
                )}
                <br />
                <ul className="mt-1 mb-0 pt-0">
                  {flagBag
                    .getFlags()
                    .map((bit) => new StatusFlag(bit))
                    .map((flag) => (
                      <li key={flag.value}>{I18n.t(flag.getReadable())}</li>
                    ))}
                </ul>
                <br />
                <small>
                  {I18n.t(
                    "trackdechets.sync_statuses_table.cell.badges.invalid.lastUpdatedAt",
                    {
                      date: formatDateTime(lastUpdatedAt),
                    },
                  )}{" "}
                </small>
              </Popover.Body>
            </Popover>
          }
        >
          <span tabIndex={1} className="badge bg-warning">
            {I18n.t(
              "trackdechets.sync_statuses_table.cell.badges.invalid.value",
            )}
          </span>
        </OverlayTrigger>
      );
      break;
  }

  return (
    <div className="trackdechets-sync-status">
      {badge}
      <button
        className="btn btn-xs refresh"
        title="Rafraîchir"
        onClick={refresh}
      >
        <i className="fa-solid fa-refresh" />
      </button>
    </div>
  );
}
